/**
 * Typed slevový kód apply on PhoneFirst payment step (plan §8 / §3.15–3.20).
 * Channel CUSTOMER_CHECKOUT; checkoutSessionPublicId = URL sessionId.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApplyPromoCodeResponse } from 'pi-kiosk-shared/promo/ApplyPromoCodeResponse';
import type { LocalizedNameMap } from 'pi-kiosk-shared';
import {
  clearPromoActivatedRewardHandoff,
  setPromoActivatedRewardHandoff,
} from 'pi-kiosk-shared/promo/promoSessionHandoff';
import { ApiError } from '../../../shared/api/ApiClient.js';
import { useApiClient } from '../../../shared/providers/AppDataProvider.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { rewardsLog } from '../logging.js';
import { applyPromoCode, removePromoCode } from '../promoCodeApplyApi.js';
import { registerCustomerTypedPromoChipClear } from '../customerTypedPromoChipClear.js';
import {
  isPromoCodeNetworkError,
  mapPromoCodeApplyError,
  type PromoCodeApplyErrorMapping,
  type PromoCodeApplyRecoveryAction,
} from '../promoCodeApplyErrors.js';
import {
  buildCustomerPromoApplyIdempotencyKey,
  buildCustomerPromoRemoveIdempotencyKey,
} from '../promoIdempotencyKey.js';

export const PROMO_CODE_APPLY_TIMEOUT_MS = 15_000;

export interface CheckoutTypedPromoCodeApplied {
  readonly eventName: string;
  readonly name: string;
  readonly nameLocales: LocalizedNameMap | null;
  readonly discountAmount: number | null;
  readonly discountBps: number | null;
  readonly rewardId: string;
}

export interface UseCheckoutTypedPromoCodeResult {
  readonly code: string;
  readonly setCode: (value: string) => void;
  readonly applied: CheckoutTypedPromoCodeApplied | null;
  readonly errorMapping: PromoCodeApplyErrorMapping | null;
  readonly isApplying: boolean;
  readonly isRemoving: boolean;
  readonly fieldEnabled: boolean;
  readonly applyDisabled: boolean;
  readonly removeDisabled: boolean;
  readonly paymentHoldActive: boolean;
  readonly applyCode: (codeOverride?: string) => Promise<void>;
  readonly removeCode: () => Promise<void>;
  readonly clearApplied: () => void;
  readonly clearError: () => void;
  readonly lastRecovery: PromoCodeApplyRecoveryAction | null;
}

function createApplyAbortSignal(timeoutMs: number): {
  readonly signal: AbortSignal;
  readonly clear: () => void;
  readonly didTimeout: () => boolean;
} {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  return {
    signal: controller.signal,
    clear: () => {
      clearTimeout(timer);
    },
    didTimeout: () => timedOut,
  };
}

export function useCheckoutTypedPromoCode(input: {
  readonly checkoutSessionPublicId: string | null;
  readonly previewNetworkFailed: boolean;
  /** Plan §3.9 — INITIATED / HARD_HELD: lock apply/remove until cancel payment. */
  readonly paymentHoldActive?: boolean;
  /** §3.9 preview/sync RELEASE observed by pricing preview — only this clears chip (not null session). */
  readonly promoClaimReleased?: boolean;
  /** SP green-light for typed apply (plan §3.15). */
  readonly promotionsProgramEnabled: boolean;
}): UseCheckoutTypedPromoCodeResult {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  const { i18n } = useTranslation('customer');
  const uiLanguage = i18n.resolvedLanguage ?? i18n.language;
  const paymentHoldActive = input.paymentHoldActive === true;
  const [code, setCodeState] = useState('');
  const [applied, setApplied] = useState<CheckoutTypedPromoCodeApplied | null>(null);
  const [errorMapping, setErrorMapping] = useState<PromoCodeApplyErrorMapping | null>(null);
  const [lastRecovery, setLastRecovery] = useState<PromoCodeApplyRecoveryAction | null>(null);
  const [applyNetworkFailed, setApplyNetworkFailed] = useState(false);
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator === 'undefined' || navigator.onLine === true,
  );
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const onOnline = (): void => {
      setIsOnline(true);
    };
    const onOffline = (): void => {
      setIsOnline(false);
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const setCode = useCallback((value: string): void => {
    setCodeState(value);
    setErrorMapping(null);
    setLastRecovery(null);
  }, []);

  const clearError = useCallback((): void => {
    setErrorMapping(null);
    setLastRecovery(null);
  }, []);

  const clearApplied = useCallback((): void => {
    setApplied(null);
    const sessionId = input.checkoutSessionPublicId?.trim();
    if (sessionId) {
      clearPromoActivatedRewardHandoff(tenantCode, sessionId);
    }
  }, [input.checkoutSessionPublicId, tenantCode]);

  // Exclusive choose-loyalty (and other cross-tree callers) clear the applied chip.
  useEffect(() => {
    registerCustomerTypedPromoChipClear(clearApplied);
    return (): void => {
      registerCustomerTypedPromoChipClear(null);
    };
  }, [clearApplied]);

  // §3.8 / §3.9 — preview RELEASE clears the chip. Do not treat "session loaded with
  // null activatedPromoRewardId" as RELEASE: that races typed apply (local chip +
  // handoff land before session GET echoes the new reward id) and wipes success UX.
  // removeCode / exclusive choose-loyalty call clearApplied directly.
  const releasedClearsChip = input.promoClaimReleased === true;
  const displayApplied = releasedClearsChip ? null : applied;

  useEffect(() => {
    if (!releasedClearsChip) {
      return;
    }
    // Defer setState out of the effect body (react-hooks/set-state-in-effect).
    queueMicrotask(() => {
      clearApplied();
    });
  }, [clearApplied, releasedClearsChip]);

  const applyMutation = useMutation({
    mutationFn: async (args: {
      readonly normalizedCode: string;
      readonly sessionId: string;
      readonly acceptLanguage: string;
    }): Promise<ApplyPromoCodeResponse> => {
      const abort = createApplyAbortSignal(PROMO_CODE_APPLY_TIMEOUT_MS);
      try {
        return await applyPromoCode(api, {
          checkoutSessionPublicId: args.sessionId,
          code: args.normalizedCode,
          idempotencyKey: buildCustomerPromoApplyIdempotencyKey(args.sessionId, args.normalizedCode),
          signal: abort.signal,
          acceptLanguage: args.acceptLanguage,
        });
      } catch (error: unknown) {
        if (abort.didTimeout()) {
          const timeoutErr = new Error('PROMO_CODE_APPLY_TIMEOUT');
          timeoutErr.name = 'TimeoutError';
          throw timeoutErr;
        }
        throw error;
      } finally {
        abort.clear();
      }
    },
  });

  const applyCode = useCallback(async (codeOverride?: string): Promise<void> => {
    if (paymentHoldActive) {
      const mapping = mapPromoCodeApplyError({ code: 'PROMO_CODE_HOLD_ACTIVE_REMOVE_FORBIDDEN' });
      setErrorMapping(mapping);
      setLastRecovery(mapping.recovery);
      return;
    }
    if (!input.promotionsProgramEnabled) {
      const mapping = mapPromoCodeApplyError({
        code: 'TENANT_ENTITLEMENT_PROMO_SALES_POINT_NOT_ALLOWED',
      });
      setErrorMapping(mapping);
      setLastRecovery(mapping.recovery);
      return;
    }
    if (inFlightRef.current || applyMutation.isPending) {
      return;
    }
    const sessionId = input.checkoutSessionPublicId?.trim() ?? null;
    if (sessionId == null || sessionId.length === 0) {
      const mapping = mapPromoCodeApplyError({ code: 'PROMO_CODE_CHECKOUT_SESSION_REQUIRED' });
      setErrorMapping(mapping);
      setLastRecovery(mapping.recovery);
      setApplied(null);
      return;
    }
    const normalized = (codeOverride ?? code).trim();
    if (normalized.length === 0) {
      const mapping = mapPromoCodeApplyError({ isEmpty: true });
      setErrorMapping(mapping);
      setLastRecovery(mapping.recovery);
      return;
    }
    if (codeOverride != null && codeOverride.trim().length > 0) {
      setCode(codeOverride.trim());
    }

    inFlightRef.current = true;
    setErrorMapping(null);
    setLastRecovery(null);
    try {
      const result = await applyMutation.mutateAsync({
        normalizedCode: normalized,
        sessionId,
        acceptLanguage: uiLanguage,
      });
      setApplyNetworkFailed(false);
      setPromoActivatedRewardHandoff(tenantCode, sessionId, result.activatedPromoRewardId);
      setApplied({
        eventName: result.eventName,
        name: result.name,
        nameLocales: result.nameLocales,
        discountAmount: result.discountAmount,
        discountBps: result.discountBps,
        rewardId: result.activatedPromoRewardId,
      });
      // Refetch payment-step preview with new handoff — no blind re-apply (§3.20).
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === 'customer' &&
            key[1] === tenantCode &&
            (key.includes('pricingPreview') ||
              key.includes('promo') ||
              key.includes('checkoutSession'))
          );
        },
      });
    } catch (error: unknown) {
      const isTimeout =
        error instanceof Error &&
        (error.name === 'TimeoutError' ||
          error.message === 'PROMO_CODE_APPLY_TIMEOUT' ||
          (error.name === 'AbortError' && !isPromoCodeNetworkError(error)));
      const isNetwork = !isTimeout && isPromoCodeNetworkError(error);
      if (isNetwork) {
        setApplyNetworkFailed(true);
      }
      const apiCode = error instanceof ApiError ? error.code : undefined;
      const mapping = mapPromoCodeApplyError({
        code: apiCode,
        isNetworkFailure: isNetwork,
        isTimeout,
      });
      setErrorMapping(mapping);
      setLastRecovery(mapping.recovery);
      if (mapping.recovery === 'clear_stale_ui') {
        setApplied(null);
        clearPromoActivatedRewardHandoff(tenantCode, sessionId);
      }
      rewardsLog.warn('Checkout typed promo apply failed', {
        operation: 'applyCode',
        code: apiCode,
        error: error instanceof Error ? error.message : String(error),
      });
      // On timeout: re-query preview/session — no blind retry reserve (§3.20).
      if (isTimeout) {
        await queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return (
              Array.isArray(key) &&
              key[0] === 'customer' &&
              key[1] === tenantCode &&
              (key.includes('pricingPreview') || key.includes('checkoutSession'))
            );
          },
        });
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [
    applyMutation,
    code,
    input.checkoutSessionPublicId,
    input.promotionsProgramEnabled,
    paymentHoldActive,
    queryClient,
    setCode,
    tenantCode,
    uiLanguage,
  ]);

  const removeMutation = useMutation({
    mutationFn: async (sessionId: string): Promise<void> => {
      await removePromoCode(api, {
        checkoutSessionPublicId: sessionId,
        idempotencyKey: buildCustomerPromoRemoveIdempotencyKey(sessionId),
      });
    },
  });

  const removeCode = useCallback(async (): Promise<void> => {
    if (paymentHoldActive) {
      const mapping = mapPromoCodeApplyError({ code: 'PROMO_CODE_HOLD_ACTIVE_REMOVE_FORBIDDEN' });
      setErrorMapping(mapping);
      setLastRecovery(mapping.recovery);
      return;
    }
    if (inFlightRef.current || removeMutation.isPending || applied === null) {
      return;
    }
    const sessionId = input.checkoutSessionPublicId?.trim() ?? null;
    if (sessionId == null || sessionId.length === 0) {
      const mapping = mapPromoCodeApplyError({ code: 'PROMO_CODE_CHECKOUT_SESSION_REQUIRED' });
      setErrorMapping(mapping);
      setLastRecovery(mapping.recovery);
      return;
    }
    inFlightRef.current = true;
    setErrorMapping(null);
    setLastRecovery(null);
    try {
      await removeMutation.mutateAsync(sessionId);
      setApplied(null);
      clearPromoActivatedRewardHandoff(tenantCode, sessionId);
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === 'customer' &&
            key[1] === tenantCode &&
            (key.includes('pricingPreview') ||
              key.includes('promo') ||
              key.includes('checkoutSession'))
          );
        },
      });
    } catch (error: unknown) {
      const isNetwork = isPromoCodeNetworkError(error);
      if (isNetwork) {
        setApplyNetworkFailed(true);
      }
      const apiCode = error instanceof ApiError ? error.code : undefined;
      const mapping = mapPromoCodeApplyError({
        code: apiCode,
        isNetworkFailure: isNetwork,
      });
      setErrorMapping(mapping);
      setLastRecovery(mapping.recovery);
      rewardsLog.warn('Checkout typed promo remove failed', {
        operation: 'removeCode',
        code: apiCode,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      inFlightRef.current = false;
    }
  }, [
    applied,
    input.checkoutSessionPublicId,
    paymentHoldActive,
    queryClient,
    removeMutation,
    tenantCode,
  ]);

  const fieldEnabled =
    input.promotionsProgramEnabled &&
    isOnline === true &&
    applyNetworkFailed === false &&
    input.previewNetworkFailed === false &&
    !paymentHoldActive;
  const sessionReady =
    input.checkoutSessionPublicId != null && input.checkoutSessionPublicId.trim().length > 0;
  const applyDisabled =
    !fieldEnabled ||
    !sessionReady ||
    applyMutation.isPending ||
    removeMutation.isPending ||
    paymentHoldActive ||
    displayApplied !== null;
  const removeDisabled =
    !fieldEnabled ||
    !sessionReady ||
    displayApplied === null ||
    applyMutation.isPending ||
    removeMutation.isPending ||
    paymentHoldActive;

  return {
    code,
    setCode,
    applied: displayApplied,
    errorMapping,
    isApplying: applyMutation.isPending,
    isRemoving: removeMutation.isPending,
    fieldEnabled,
    applyDisabled,
    removeDisabled,
    paymentHoldActive,
    applyCode,
    removeCode,
    clearApplied,
    clearError,
    lastRecovery,
  };
}
